import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Chip, CircularProgress, InputAdornment, Paper, Stack, TextField, Typography } from '@mui/material';
import { Event as EventIcon, Place, Search } from '@mui/icons-material';
import { format } from 'date-fns';
import AppNavigation from './AppNavigation';
import SignInNudge from './SignInNudge';
import { authService } from '../services/authService';
import { buddyService, UserPublicProfile } from '../services/buddyService';
import { userProfileService, RegionCounts } from '../services/userProfileService';
import { eventService, DiveEvent } from '../services/eventService';
import { useDiveSiteCatalog } from '../hooks/useDiveSiteCatalog';
import { buildCountries, buildRegions, membershipsOf, regionForHomeSite, regionLabel } from '../utils/regions';

/** /communities — every dive region in the catalog as a community, with how many local divers each has. */
const CommunitiesPage: React.FC = () => {
  const navigate = useNavigate();
  const { allSites } = useDiveSiteCatalog();
  const [currentUser, setCurrentUser] = useState(authService.getUser());
  const userId = currentUser?.userId ?? null;
  const [counts, setCounts] = useState<RegionCounts>({});
  const [countryCounts, setCountryCounts] = useState<Record<string, number>>({});
  const [myProfile, setMyProfile] = useState<UserPublicProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [events, setEvents] = useState<DiveEvent[]>([]);
  const [showAllEvents, setShowAllEvents] = useState(false);

  useEffect(() => {
    if (!userId) { setLoading(false); return; }
    let cancelled = false;
    Promise.allSettled([userProfileService.getRegionCounts(), buddyService.getUserProfile(userId), userProfileService.getCountryCounts()]).then(([c, me, cc]) => {
      if (cancelled) return;
      if (c.status === 'fulfilled') setCounts(c.value);
      if (cc.status === 'fulfilled') setCountryCounts(cc.value);
      if (me.status === 'fulfilled') setMyProfile(me.value);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [userId]);

  // Best-effort: the page is fully usable without the events list.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    eventService.getEventsForMyCommunities().then(list => { if (!cancelled) setEvents(list); }).catch(() => {});
    return () => { cancelled = true; };
  }, [userId]);

  const regions = useMemo(() => buildRegions(allSites), [allSites]);

  // Members per region (home-site divers plus those who joined) straight from the server's region index.
  const diverCounts = useMemo(
    () => new Map(Object.entries(counts).map(([slug, c]) => [slug, c.locals + c.joined])),
    [counts],
  );

  const myHomeRegion = useMemo(() => myProfile ? regionForHomeSite(myProfile, regions) : null, [myProfile, regions]);
  const myRegions = useMemo(() => myProfile ? membershipsOf(myProfile, regions) : [], [myProfile, regions]);

  const countries = useMemo(() => buildCountries(regions), [regions]);
  const visibleCountries = useMemo(() => {
    const q = query.trim().toLowerCase();
    return countries.filter(c => !q || c.name.toLowerCase().includes(q));
  }, [countries, query]);

  const regionNames = useMemo(() => new Map(regions.map(r => [r.slug, regionLabel(r)])), [regions]);
  const shownEvents = showAllEvents ? events : events.slice(0, 5);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return regions.filter(r => !q || regionLabel(r).toLowerCase().includes(q));
  }, [regions, query]);

  if (!userId) {
    return (
      <Box>
        <AppNavigation />
        <SignInNudge
          feature="explore dive communities"
          description="Every dive region has its own community — the local divers, their public posts, and the clubs and groups nearby. Sign in or create a free account to join in."
          onSignedIn={() => setCurrentUser(authService.getUser())}
        />
      </Box>
    );
  }

  return (
    <Box>
      <AppNavigation />
      <Box sx={{ px: { xs: 2, sm: 3 }, pb: 4, maxWidth: 1000, mx: 'auto' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mt: 3, mb: 0.5 }}>Dive Communities</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Meet the divers around every dive region. Set your home dive site in your profile, or open any community and join it.
        </Typography>

        {myRegions.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="overline" color="primary">Your communities</Typography>
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
              {myRegions.map(r => {
                const count = diverCounts.get(r.slug) ?? 0;
                return (
                  <Paper
                    key={r.slug}
                    variant="outlined"
                    onClick={() => navigate(`/communities/${r.slug}`)}
                    sx={{ p: 2, cursor: 'pointer', borderColor: 'primary.main', '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{regionLabel(r)}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {r.slug === myHomeRegion?.slug ? 'Your home region · ' : 'Joined · '}
                      {count} member{count === 1 ? '' : 's'} · {r.sites.length} dive sites
                    </Typography>
                  </Paper>
                );
              })}
            </Box>
          </Box>
        )}

        {events.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="overline" color="primary">Upcoming in your communities</Typography>
            <Stack spacing={1}>
              {shownEvents.map(ev => (
                <Paper
                  key={ev.eventId}
                  variant="outlined"
                  onClick={() => ev.regionSlug && navigate(`/communities/${ev.regionSlug}`)}
                  sx={{ p: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <EventIcon color="primary" fontSize="small" />
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{ev.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {format(new Date(ev.startTime), 'EEE, MMM d · h:mm a')} · {regionNames.get(ev.regionSlug ?? '') ?? 'Community'} · {ev.goingCount} going
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              ))}
              {events.length > 5 && (
                <Typography
                  variant="caption"
                  color="primary"
                  sx={{ cursor: 'pointer', alignSelf: 'flex-start' }}
                  onClick={() => setShowAllEvents(v => !v)}
                >
                  {showAllEvents ? 'Show fewer' : `Show ${events.length - 5} more`}
                </Typography>
              )}
            </Stack>
          </Box>
        )}

        <TextField
          fullWidth
          size="small"
          placeholder="Search regions or countries…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          sx={{ mb: 2 }}
        />

        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>}

        {visibleCountries.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="overline" color="primary">Browse by country</Typography>
            <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
              {visibleCountries.slice(0, query.trim() ? undefined : 24).map(c => (
                <Chip
                  key={c.slug}
                  clickable
                  variant="outlined"
                  onClick={() => navigate(`/countries/${c.slug}`)}
                  label={countryCounts[c.slug] ? `${c.name} · ${countryCounts[c.slug]}` : c.name}
                />
              ))}
            </Stack>
          </Box>
        )}

        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
          {visible.map(r => {
            const count = diverCounts.get(r.slug) ?? 0;
            return (
              <Paper
                key={r.slug}
                variant="outlined"
                onClick={() => navigate(`/communities/${r.slug}`)}
                sx={{ p: 2, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Place fontSize="small" color="primary" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{regionLabel(r)}</Typography>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Chip size="small" variant="outlined" label={`${r.sites.length} site${r.sites.length === 1 ? '' : 's'}`} />
                  {count > 0 && <Chip size="small" color="primary" variant="outlined" label={`${count} member${count === 1 ? '' : 's'}`} />}
                </Stack>
              </Paper>
            );
          })}
        </Box>
        {visible.length === 0 && !loading && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>No regions match "{query}".</Typography>
        )}
      </Box>
    </Box>
  );
};

export default CommunitiesPage;
